import type { AccessDetailsDialogProps } from '@confluence/space-access-details/entry-points/AccessDetailsDialog';

import type { CloseConfirmationDialogProps } from '../controlled/CloseConfirmationDialog';
import type { DialogsProps } from '../controlled/Dialogs';
import type { GroupNotifyWarningDialogProps } from '../controlled/GroupNotifyWarningDialog';
import type { ShareAndRestrictDialogProps } from '../controlled/shareAndRestrictDialog/ShareAndRestrictDialog';
import type { InspectPermsDialogProps } from '../controlled/shareAndRestrictDialog/header/OverflowMenu';
import type { RestrictedAccessWarningDialogProps } from '../controlled/resolveRestrictions/RestrictedAccessWarningDialog';
import type { ResolveRestrictionsDialogProps } from '../controlled/resolveRestrictions/ResolveRestrictionsDialog';
import type { OpenAccessWarningDialogProps } from '../controlled/openAccessWarning/OpenAccessWarningDialog';
import type { GroupOrTeamPrincipal, Principal } from '../controlled/commonTypes';

import { makeHeaderProps, makeGeneralAccessProps } from './makeHeaderProps';
import { makeBodyProps } from './makeBodyProps';
import { makeFooterProps } from './makeFooterProps';
import { type StatesAndActions, SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from './Controller';

export function makeDialogsProps(statesAndActions: StatesAndActions): DialogsProps {
	const {
		masterState: {
			sessionState,
			isCloseConfirmationOpen,
			isSpaceAccessDetailsOpen,
			isInspectPermsDialogOpen,
			isOpenAccessWarningDialogOpen,
			isAccessRequestsWarnRestrictionsDialogOpen,
			isAccessRequestsResolveRestrictionsDialogOpen,
		},
	} = statesAndActions;

	return {
		shareAndRestrictDialog:
			sessionState === 'shareAndRestrict'
				? makeShareAndRestrictDialogProps(statesAndActions)
				: undefined,
		restrictedAccessWarningDialog:
			sessionState === 'warnRestrictedAccess' || isAccessRequestsWarnRestrictionsDialogOpen
				? makeRestrictedAccessWarningDialog(statesAndActions)
				: undefined,
		groupNotifyWarningDialog:
			sessionState === 'confirmGroupNotifications'
				? makeGroupNotifyWarningDialog(statesAndActions)
				: undefined,
		resolveRestrictionsDialog:
			sessionState === 'resolveRestrictions' || isAccessRequestsResolveRestrictionsDialogOpen
				? makeResolveRestrictionsDialogProps(statesAndActions)
				: undefined,
		closeConfirmationDialog: isCloseConfirmationOpen
			? makeCloseConfirmationProps(statesAndActions)
			: undefined,
		spaceAccessDetails: isSpaceAccessDetailsOpen
			? makeSpaceAccessDetailsProps(statesAndActions)
			: undefined,
		inspectPermsDialog: isInspectPermsDialogOpen
			? makeInspectPermsDialogProps(statesAndActions)
			: undefined,
		openAccessWarningDialog: isOpenAccessWarningDialogOpen
			? makeOpenAccessWarningDialogProps(statesAndActions)
			: undefined,
		// close confirmation, public linkDialogProps, etc to follow
	};
}

function makeShareAndRestrictDialogProps(
	statesAndActions: StatesAndActions,
): ShareAndRestrictDialogProps {
	const {
		actions: { dismiss, potentialFinalDialogDidCompleteClose, openAccessRequestsDialog },
		masterState: { isPublicLinkDialogOpen, isShareToSlackDialogOpen, isAccessRequestsDialogOpen },
		derivedState: { isUnpublishedDraft, stagedGeneralAccessLevel },
		queriesState: { hasInheritedRestrictions },
		controllerProps: {
			contentId,
			contentType,
			contentSubType,
			pageMode,
			openFrom,
			showDialogNudgeSpotlight,
			initialDialog,
			onInitialDialogOpen,
		},
	} = statesAndActions;

	const isAnotherDialogOpen =
		isPublicLinkDialogOpen || isShareToSlackDialogOpen || isAccessRequestsDialogOpen;

	const hideFooter = isAnotherDialogOpen;

	return {
		header: makeHeaderProps(statesAndActions),
		body: makeBodyProps(statesAndActions),
		footer: hideFooter ? undefined : makeFooterProps(statesAndActions),
		onCloseRequest: dismiss,
		onCloseComplete: potentialFinalDialogDidCompleteClose,
		showDialogNudgeSpotlight,
		openAccessRequestsDialog: (initialDialog && openAccessRequestsDialog) ?? undefined,
		onInitialDialogOpen,
		analyticsAttributes: {
			contentId,
			contentType,
			contentSubType,
			isLivePage: contentSubType === 'live',
			isDraft: isUnpublishedDraft,
			pageMode,
			generalAccessLevel: stagedGeneralAccessLevel,
			hasInheritedRestrictions,
			openFrom,
		},
	};
}

function makeGroupNotifyWarningDialog(
	statesAndActions: StatesAndActions,
): GroupNotifyWarningDialogProps {
	const {
		masterState: { isSaving },
		derivedState: { stagedNotificationRecipients },
		actions: {
			doNotifyGroupsConfirmed,
			doNotNotifyGroupsConfirmed,
			potentialFinalDialogDidCompleteClose,
		},
	} = statesAndActions;

	const groupNames = stagedNotificationRecipients
		.filter(({ type }) => type === 'group')
		.map(({ name }) => name);

	const groupList: GroupOrTeamPrincipal[] = stagedNotificationRecipients.filter(
		(recipient): recipient is Extract<Principal, { type: 'group' | 'team' }> =>
			recipient.type === 'group' || recipient.type === 'team',
	);

	return {
		groupNames,
		groupList,
		isSaving,
		onDoNotify: doNotifyGroupsConfirmed,
		onDoNotNotify: doNotNotifyGroupsConfirmed,
		onCloseComplete: potentialFinalDialogDidCompleteClose,
	};
}

function makeRestrictedAccessWarningDialog(
	statesAndActions: StatesAndActions,
): RestrictedAccessWarningDialogProps | undefined {
	const {
		controllerProps: { pageTitle },
		masterState: { restrictedUserPrincipals, isAccessRequestsWarnRestrictionsDialogOpen },
		actions: {
			ignoreRestrictionsAndContinue,
			potentialFinalDialogDidCompleteClose,
			closeAccessRequestsWarnRestrictionsDialog,
		},
	} = statesAndActions;

	if (isAccessRequestsWarnRestrictionsDialogOpen) {
		return {
			restrictedUserPrincipals,
			pageTitle,
			onSkip: closeAccessRequestsWarnRestrictionsDialog,
			triggeredBy: 'accessRequests',
			onCloseComplete: () => {},
		};
	}

	return {
		restrictedUserPrincipals,
		pageTitle,
		triggeredBy: 'share',
		onSkip: ignoreRestrictionsAndContinue,
		onCloseComplete: potentialFinalDialogDidCompleteClose,
	};
}

function makeCloseConfirmationProps(
	statesAndActions: StatesAndActions,
): CloseConfirmationDialogProps {
	const {
		actions: { dismissCloseConfirmationDialog, closeWithoutSaving },
		controllerProps: { focusRef },
	} = statesAndActions;
	return {
		onCancel: dismissCloseConfirmationDialog,
		onDiscard: closeWithoutSaving,
		focusRef,
	};
}

function makeSpaceAccessDetailsProps(statesAndActions: StatesAndActions): AccessDetailsDialogProps {
	const { controllerProps, actions, queriesState } = statesAndActions;
	return {
		onClose: actions.dismissSpaceAccessDetails,
		accessDetailsParams: {
			restrictingContentId: controllerProps.contentId,
			spaceKey: queriesState.spaceKey,
			hasInheritedRestrictions: queriesState.hasInheritedRestrictions,
			experience: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
		},
	};
}

function makeInspectPermsDialogProps(statesAndActions: StatesAndActions): InspectPermsDialogProps {
	const { controllerProps, actions } = statesAndActions;
	return {
		onClose: actions.dismissInspectPermsDialog,
		contentId: controllerProps.contentId,
	};
}

function makeResolveRestrictionsDialogProps(
	statesAndActions: StatesAndActions,
): ResolveRestrictionsDialogProps | undefined {
	const {
		controllerProps: { pageTitle, contentId },
		masterState: {
			restrictedUserPrincipals,
			isAccessRequestsResolveRestrictionsDialogOpen,
			assignableSpaceRolesIntersection,
			addMode,
			accessRequestsCurrentUserAddMode,
		},
		actions: {
			resolveRestrictionsComplete,
			potentialFinalDialogDidCompleteClose,
			ignoreRestrictionsAndContinue,
			showErrorFlag,
			closeAccessRequestsResolveRestrictionsDialog,
		},
	} = statesAndActions;

	if (isAccessRequestsResolveRestrictionsDialogOpen) {
		// This is mainly to satisfy the compiler
		// In practice we shouldn't have a case where Resolve Restrictions Dialog
		// Is open for Access Requests and the add mode is not specified
		if (!accessRequestsCurrentUserAddMode) {
			return;
		}

		return {
			contentId,
			pageTitle,
			restrictedUserPrincipals,
			assignableSpaceRolesIntersection,
			addMode: accessRequestsCurrentUserAddMode,
			triggeredBy: 'accessRequests',
			onComplete: closeAccessRequestsResolveRestrictionsDialog,
			onCloseComplete: () => {},
			onSkip: closeAccessRequestsResolveRestrictionsDialog,
			showErrorFlag,
		};
	}

	return {
		contentId,
		pageTitle,
		restrictedUserPrincipals,
		assignableSpaceRolesIntersection,
		addMode,
		triggeredBy: 'share',
		onComplete: resolveRestrictionsComplete,
		onCloseComplete: potentialFinalDialogDidCompleteClose,
		onSkip: ignoreRestrictionsAndContinue,
		showErrorFlag,
	};
}

function makeOpenAccessWarningDialogProps(
	statesAndActions: StatesAndActions,
): OpenAccessWarningDialogProps {
	const {
		actions: { closeOpenAccessWarningDialog, closeWithoutSaving },
		controllerProps: { contentId, refetchButtonData },
		controllerExtras: { successFlag, errorFlag },
	} = statesAndActions;

	const closeShareAndRestricDialog = () => {
		closeWithoutSaving();
		refetchButtonData();
	};

	return {
		generalAccess: makeGeneralAccessProps(statesAndActions),
		closeOpenAccessWarningDialog,
		successFlag,
		errorFlag,
		closeShareAndRestricDialog,
		contentId,
	};
}
